/**
 * Shared form metadata + published-definition loading for the data-io feature.
 * The export page, the import wizard, and validation all need the published
 * field list (+ conditional rules) of a target form.
 */
import { supabase } from '@/lib/supabase/client'
import { parseDefinition } from '@/features/form-builder/api/form-builder-api'
import type {
  FieldDefinition,
  ConditionalRule,
  LocalizedString,
} from '@/features/form-builder/api/form-builder-types'

export interface DataIoForm {
  id: string
  formKey: string
  title: string
  groupTitle: string
  instanceType: 'single' | 'repeatable'
  currentVersionId: string | null
  isPublished: boolean
}

function fa(v: unknown): string {
  if (v && typeof v === 'object' && 'fa' in (v as Record<string, unknown>)) {
    return (v as LocalizedString).fa ?? ''
  }
  return typeof v === 'string' ? v : ''
}

/** Published forms in a project, grouped-label included, for the form picker. */
export async function fetchProjectForms(projectId: string): Promise<DataIoForm[]> {
  const [formsRes, groupsRes] = await Promise.all([
    supabase
      .from('forms')
      .select('id, form_key, title, group_id, instance_type, current_version_id, is_published')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .order('display_order'),
    supabase
      .from('form_groups')
      .select('id, title')
      .eq('project_id', projectId)
      .eq('is_deleted', false),
  ])
  if (formsRes.error) throw formsRes.error
  if (groupsRes.error) throw groupsRes.error

  const groupTitle = new Map((groupsRes.data ?? []).map((g) => [g.id, fa(g.title)]))
  return (formsRes.data ?? []).map((f) => ({
    id: f.id,
    formKey: f.form_key,
    title: fa(f.title),
    groupTitle: groupTitle.get(f.group_id) ?? '',
    instanceType: f.instance_type as 'single' | 'repeatable',
    currentVersionId: f.current_version_id,
    isPublished: f.is_published,
  }))
}

export interface LoadedFormDefinition {
  formId: string
  formVersionId: string
  versionLabel: string
  fields: FieldDefinition[]
  conditionalRules: ConditionalRule[]
}

/**
 * Load a form's CURRENT PUBLISHED definition (the version imported data should
 * validate against). Throws when the form has never been published.
 */
export async function loadPublishedDefinition(formId: string): Promise<LoadedFormDefinition> {
  const { data: form, error: formErr } = await supabase
    .from('forms')
    .select('current_version_id')
    .eq('id', formId)
    .single()
  if (formErr) throw formErr
  if (!form.current_version_id) {
    throw new Error('این فرم هنوز منتشر نشده است؛ ابتدا یک نسخه منتشر کنید.')
  }

  const { data: ver, error: verErr } = await supabase
    .from('form_versions')
    .select('id, definition_snapshot, version_major, version_minor')
    .eq('id', form.current_version_id)
    .single()
  if (verErr) throw verErr

  const def = parseDefinition(ver.definition_snapshot)
  return {
    formId,
    formVersionId: ver.id,
    versionLabel: `${ver.version_major}.${ver.version_minor}`,
    fields: def.fields,
    conditionalRules: def.conditional_rules,
  }
}
